// src/utils/theme.js
import { createTheme } from '@mui/material/styles';
import { getPreset, buildPresetFromHex, CUSTOM_PRESET_KEY } from '../setting/presets';
import { getFont, DEFAULT_FONT } from '../setting/fonts';

// Secondary stays as the existing brand-darker variant; the user-visible
// "primary" is now driven by the chosen preset.
const SECONDARY_LIGHT = '#1565C0';
const SECONDARY_DARK = '#1F2937';

const buildTypography = (fontFamily) => ({
    fontFamily,
    fontWeight: 600,
    h1: { fontFamily, fontWeight: 'bold' },
    h2: { fontFamily, fontWeight: 600 },
    h3: { fontFamily, fontWeight: 500 },
    h4: { fontFamily, fontWeight: 400 },
    h5: { fontFamily, fontWeight: 400 },
    h6: { fontFamily, fontWeight: 400 },
});

const buildLightPalette = (preset) => ({
    mode: 'light',
    primary: {
        main: preset.main,
        light: preset.light,
        dark: preset.dark,
        contrastText: preset.contrastText,
    },
    secondary: { main: SECONDARY_LIGHT },
    nav: {
        main: '#FFFFFF',
        link: '#4A5568',
        hover: preset.main,
        btn: SECONDARY_LIGHT,
    },
    footer: { main: '#0D1B2A', link: '#fff', hover: '#A69F9F' },
    background: {
        default: '#E9EEF5',
        section: '#F1F4FA',
        card: '#FFFFFF',
        input: '#FFFFFF',
        form: '#FFFFFF',
    },
    divider: '#D0D7E2',
    boxShadow: '0px 4px 10px 0px rgba(15, 23, 42, 0.12)',
    editor: {
        toolbar: '#F4F6FB',
        background: '#FFFFFF',
        border: '#E0E6F0',
        inputBackground: '#FFFFFF',
        placeholder: '#94A3B8',
        divider: '#E2E8F0',
    },
});

const buildDarkPalette = (preset) => ({
    mode: 'dark',
    primary: {
        main: preset.main,
        light: preset.light,
        dark: preset.dark,
        contrastText: preset.contrastText,
    },
    secondary: { main: SECONDARY_DARK },
    nav: {
        main: '#020617',
        link: '#E5E7EB',
        hover: preset.main,
        btn: '#FFFFFF',
    },
    footer: { main: '#020617', link: '#FFFFFF', hover: '#A69F9F' },
    background: {
        default: '#020617',
        section: '#050A15',
        card: '#0F172A',
        input: '#020617',
        form: '#020617',
    },
    divider: '#1E293B',
    boxShadow: '0px 4px 10px 0px rgba(15, 23, 42, 0.6)',
    editor: {
        toolbar: '#020617',
        background: '#020617',
        border: '#1E293B',
        inputBackground: '#020617',
        placeholder: '#6B7280',
        divider: '#1F2937',
    },
});

const buildPalette = (mode, preset) =>
    mode === 'dark' ? buildDarkPalette(preset) : buildLightPalette(preset);

/**
 * Build a MUI theme tied to the current Settings state.
 *
 * @param {object} opts
 * @param {'light'|'dark'} [opts.mode='light']
 * @param {string} [opts.presetKey='default']  Key from setting/presets.js,
 *   or CUSTOM_PRESET_KEY ('custom') to use opts.customColor instead.
 * @param {string|null} [opts.customColor=null] Hex color used when
 *   presetKey === CUSTOM_PRESET_KEY. Falls back to the default preset
 *   if the hex is missing or invalid.
 * @param {string} [opts.fontKey='publicSans'] Key from setting/fonts.js
 * @param {'ltr'|'rtl'} [opts.direction='ltr']
 */
const theme = ({
    mode = 'light',
    presetKey = 'default',
    customColor = null,
    fontKey = DEFAULT_FONT,
    direction = 'ltr',
} = {}) => {
    let preset;
    if (presetKey === CUSTOM_PRESET_KEY) {
        preset = buildPresetFromHex(customColor) || getPreset('default');
    } else {
        preset = getPreset(presetKey);
    }
    const font = getFont(fontKey);
    return createTheme({
        direction,
        palette: buildPalette(mode, preset),
        typography: buildTypography(font.stack),
    });
};

export default theme;
